/**
 * Dashboard store — manages the active marketing dashboard state.
 * Layout changes happen in-memory; the user explicitly saves via toolbar.
 */

import { create } from 'zustand'
import type { Dashboard, DashboardWidget, DashboardSummary, WidgetConfig } from '@/types/dashboard'

interface DashboardState {
  /** Full current dashboard being viewed/edited */
  currentDashboard: Dashboard | null
  /** List of user's dashboards (summary only) */
  dashboards: DashboardSummary[]
  /** Whether the dashboard is in edit/layout mode */
  isEditMode: boolean
  /** Loading state */
  isLoading: boolean
  /** Widget currently being configured (by ID) */
  configuringWidgetId: string | null
  /** Whether there are unsaved changes */
  isDirty: boolean

  // Setters
  setCurrentDashboard: (d: Dashboard | null) => void
  setDashboards: (list: DashboardSummary[]) => void
  setEditMode: (edit: boolean) => void
  setLoading: (loading: boolean) => void
  setConfiguringWidgetId: (id: string | null) => void

  // Widget mutations (optimistic, in-memory)
  addWidget: (widget: DashboardWidget) => void
  removeWidget: (widgetId: string) => void
  updateWidgetConfig: (widgetId: string, config: Partial<WidgetConfig>) => void
  updateWidgetTitle: (widgetId: string, title: string) => void
  updateLayouts: (layouts: Array<{ id: string; x: number; y: number; w: number; h: number }>) => void
  updateGlobalDateRange: (days: number) => void
  updateDashboardName: (name: string) => void
  markClean: () => void

  reset: () => void
}

const initialState = {
  currentDashboard: null,
  dashboards: [],
  isEditMode: false,
  isLoading: false,
  configuringWidgetId: null,
  isDirty: false,
}

export const useDashboardStore = create<DashboardState>()((set) => ({
  ...initialState,

  setCurrentDashboard: (d) => set({ currentDashboard: d, isDirty: false }),
  setDashboards: (list) => set({ dashboards: list }),
  setEditMode: (edit) => set({ isEditMode: edit }),
  setLoading: (loading) => set({ isLoading: loading }),
  setConfiguringWidgetId: (id) => set({ configuringWidgetId: id }),

  addWidget: (widget) =>
    set((state) => {
      if (!state.currentDashboard) return state
      return {
        currentDashboard: {
          ...state.currentDashboard,
          widgets: [...state.currentDashboard.widgets, widget],
        },
        isDirty: true,
      }
    }),

  removeWidget: (widgetId) =>
    set((state) => {
      if (!state.currentDashboard) return state
      return {
        currentDashboard: {
          ...state.currentDashboard,
          widgets: state.currentDashboard.widgets.filter((w) => w.id !== widgetId),
        },
        isDirty: true,
      }
    }),

  updateWidgetConfig: (widgetId, config) =>
    set((state) => {
      if (!state.currentDashboard) return state
      return {
        currentDashboard: {
          ...state.currentDashboard,
          widgets: state.currentDashboard.widgets.map((w) =>
            w.id === widgetId ? { ...w, config: { ...w.config, ...config } } : w,
          ),
        },
        isDirty: true,
      }
    }),

  updateWidgetTitle: (widgetId, title) =>
    set((state) => {
      if (!state.currentDashboard) return state
      return {
        currentDashboard: {
          ...state.currentDashboard,
          widgets: state.currentDashboard.widgets.map((w) =>
            w.id === widgetId ? { ...w, title } : w,
          ),
        },
        isDirty: true,
      }
    }),

  updateLayouts: (layouts) =>
    set((state) => {
      if (!state.currentDashboard) return state
      const layoutMap = new Map(layouts.map((l) => [l.id, l]))
      return {
        currentDashboard: {
          ...state.currentDashboard,
          widgets: state.currentDashboard.widgets.map((w) => {
            const newLayout = layoutMap.get(w.id)
            if (!newLayout) return w
            return { ...w, layout: { x: newLayout.x, y: newLayout.y, w: newLayout.w, h: newLayout.h } }
          }),
        },
        isDirty: true,
      }
    }),

  updateGlobalDateRange: (days) =>
    set((state) => {
      if (!state.currentDashboard) return state
      return {
        currentDashboard: { ...state.currentDashboard, globalDateRange: days },
        isDirty: true,
      }
    }),

  updateDashboardName: (name) =>
    set((state) => {
      if (!state.currentDashboard) return state
      return {
        currentDashboard: { ...state.currentDashboard, name },
        isDirty: true,
      }
    }),

  markClean: () => set({ isDirty: false }),

  reset: () => set(initialState),
}))
